/**
 * CENTRAL GAME REGISTRY
 *
 * This file automatically imports all game configurations.
 * To add a new game:
 * 1. Create a folder: /games/your-game/
 * 2. Create config.ts with the game config
 * 3. Add import below
 * 4. Add to GAMES array
 *
 * Everything else will work automatically!
 */

import { onePieceGame } from "./one-piece/config";
import { unionArenaGame } from "./union-arena/config";
import { riftboundGame } from "./riftbound/config";
import { narutoGame } from "./naruto/config";
import { palworldGame } from "./palworld/config";
import type { DecklistParam } from "@/models/decklist";
import type { GameHandler } from "@/interfaces/GameHandler";

// ============================================================================
// ALL GAMES - Add your new game here!
// ============================================================================
export const GAMES = [
  onePieceGame,
  unionArenaGame,
  riftboundGame,
  narutoGame,
  palworldGame,
] as const;

// ============================================================================
// AUTO-GENERATED TYPES & UTILITIES
// ============================================================================

// Extract game IDs from the configs
export type SupportedGames = (typeof GAMES)[number]["id"];

// Create a map for easy lookup
export const GAME_MAP = GAMES.reduce(
  (map, game) => {
    map[game.id] = game;
    return map;
  },
  {} as Record<SupportedGames, (typeof GAMES)[number]>,
);

// ============================================================================
// GAME INFO HELPERS (replaces lib/gameConfig.ts)
// ============================================================================

export function getGame(id: SupportedGames) {
  return GAME_MAP[id];
}

/** Whether `id` is a registered game. `[gameName]` matches any path segment, so
 *  unrelated URLs (ad callbacks like /cookie_sync, typos) land there too. */
export function isSupportedGame(id: string): id is SupportedGames {
  return Object.prototype.hasOwnProperty.call(GAME_MAP, id);
}

/**
 * Returns undefined for an unregistered id rather than throwing. Callers
 * already guard with `if (!config)`; this used to dereference `game.seo` on
 * undefined first, turning every unknown URL into a 500.
 */
export function getGameInfo(id: SupportedGames) {
  const game = getGame(id);
  if (!game) return undefined;
  return {
    title: game.seo.title,
    description: game.seo.description,
    formats: game.formats,
    gameName: game.name,
    gameAbbreviation: game.abbreviation,
    hasLeaderDistribution: game.mechanics.hasLeaderDistribution,
    hasPieChart: game.mechanics.hasPieChart,
    hasFormatReleaseDates: game.mechanics.hasFormatReleaseDates,
  };
}

export function getDecklistParams(gameName: SupportedGames): DecklistParam[] {
  const game = getGame(gameName);
  const decklistParams: DecklistParam[] = [];

  // Add global region formats if they exist (takes precedence)
  const formats = game.formats as {
    east: string[];
    west?: string[];
    global?: string[];
  };
  if (formats.global && formats.global.length > 0) {
    formats.global.forEach((format: string) => {
      decklistParams.push({ region: "global", format });
    });
    return decklistParams;
  }

  // Add east region formats
  formats.east.forEach((format) => {
    decklistParams.push({ region: "east", format });
  });

  // Add west region formats if they exist
  if (formats.west && formats.west.length > 0) {
    formats.west.forEach((format) => {
      decklistParams.push({ region: "west", format });
    });
  }

  return decklistParams;
}

export function getAllGames() {
  return GAMES;
}

export function getGamesByDomain(domain: "gumgum" | "tcgfan") {
  return GAMES.filter((game) => game.domain.allowedOn === domain);
}

// ============================================================================
// GAME HANDLER REGISTRY (replaces registry/gameHandlerRegistry.ts)
// ============================================================================

const handlerCache = new Map<SupportedGames, GameHandler>();

/**
 * Typed on purpose: this used to return `any`, so every call site silently
 * opted out of type checking and a game could omit a required method without
 * the compiler noticing.
 */
export function getGameHandler(id: SupportedGames): GameHandler {
  let handler = handlerCache.get(id);
  if (!handler) {
    handler = getGame(id).handler() as GameHandler;
    handlerCache.set(id, handler);
  }
  return handler;
}

// ============================================================================
// DECK BUILDER REGISTRY (replaces components/DeckBuilder/gameRegistry.ts)
// ============================================================================

export function getGameDeckBuilderConfig(id: SupportedGames) {
  const game = getGame(id);
  return {
    getCardData: game.deckBuilder.getCardData,
    getAACardIdData: game.deckBuilder.getAACardIdData,
    getCardImage: game.deckBuilder.getCardImage,
    getAAFromCardId: game.deckBuilder.getAAFromCardId,
    leaderType: game.mechanics.leaderType,
    maxCopiesDefault: game.mechanics.maxCopiesDefault,
    hasLifeProperty: game.mechanics.hasLifeProperty,
  };
}

/**
 * TCGplayer identifiers for a game, or undefined if it is not sold there.
 * Previously this table was copy-pasted in three files and silently defaulted
 * every unlisted game to One Piece's category id.
 */
export function getTcgplayerConfig(id: SupportedGames) {
  return (
    getGame(id) as { tcgplayer?: { categoryId: number; productLine: string } }
  ).tcgplayer;
}

/** The game a numeric TCGplayer category id belongs to, if any. */
export function getGameByTcgplayerCategoryId(
  categoryId: number | undefined | null,
): SupportedGames | undefined {
  if (categoryId == null) return undefined;
  return GAMES.find(
    (g) =>
      (g as { tcgplayer?: { categoryId: number } }).tcgplayer?.categoryId ===
      categoryId,
  )?.id;
}

/** URL prefix for a game's routes ("" for the domain's root game). */
export function getRoutePrefix(id: SupportedGames): string {
  return (getGame(id) as { routePrefix?: string }).routePrefix ?? `${id}/`;
}

// ============================================================================
// NAVIGATION CONFIG (replaces navbar gameNavigationConfig)
// ============================================================================

export function getGameNavigationConfig(id: SupportedGames) {
  const game = getGame(id);
  return game.navigation;
}

// ============================================================================
// STORE UTILITIES
// ============================================================================

// Create initial state for all stores (auto-generated from games)
export function createInitialStoreState<T>(defaultValue: T) {
  return GAMES.reduce(
    (state, game) => {
      state[game.id] = defaultValue;
      return state;
    },
    {} as Record<SupportedGames, T>,
  );
}

// Create initial state with per-game defaults
export function createGameStoreState<T>(
  getValue: (game: (typeof GAMES)[number]) => T,
) {
  return GAMES.reduce(
    (state, game) => {
      state[game.id] = getValue(game);
      return state;
    },
    {} as Record<SupportedGames, T>,
  );
}

// ============================================================================
// EXPORTS
// ============================================================================

// Export individual games for direct access
export {
  onePieceGame,
  unionArenaGame,
  riftboundGame,
  narutoGame,
  palworldGame,
};
